import React from 'react';
import ProjectDetail from './ProjectDetail';
import { projectMeta } from './data';
import ProgressiveImg from '../components/ProgressiveImg';
import { toCdn } from '../utils/cdn';
import Figure from '../components/Figure';

export default function Archive() {
  const meta = projectMeta.archive;
  // Curated order: mix images and videos in the exact sequence desired
  const items = [
    { type: 'image', name: 'swiftIcon', alt: 'Swift Icon' },
    { type: 'image', name: 'aspectShotIcon', alt: 'Screenshot App Icon' },
    { type: 'image', name: 'aspectShot', alt: 'Screenshot UI' },
    { type: 'video', name: 'reactButtons', alt: 'Custom React Buttons' },
    { type: 'image', name: 'wand', alt: 'Magic Wand Icon' },
    { type: 'image', name: 'visualCenter', alt: 'Visual Center Plugin' },
    { type: 'image', name: 'nestedCorners', alt: 'Nested Corners Plugin' },
    { type: 'image', name: 'sonar', alt: 'Sonar Icon' },
    { type: 'image', name: 'nintendoIcons', alt: 'Nintendo Icons' },
    { type: 'image', name: 'dispatch', alt: 'Dispatch Logo' },
    { type: 'video', name: 'particles', alt: 'Particle Effect' },
    { type: 'image', name: 'hal', alt: 'Hal' },
    { type: 'image', name: 'iwatch', alt: 'Apple Watch Render' },
    { type: 'video', name: 'shader1', alt: 'Shader Experiemnt' },
    { type: 'video', name: 'shader2', alt: 'Shader Experiemnt' },
    { type: 'video', name: 'walk', alt: 'Framer Walk Cycle' },
    { type: 'video', name: 'burger', alt: 'Burger Menu' },
    { type: 'image', name: 'whitney', alt: 'Mt. Whitney Coaster' },
    { type: 'image', name: 'comb', alt: 'Surfing Wax Comb' },
    { type: 'image', name: 'wallet', alt: 'Card Holder' },
    { type: 'image', name: 'wetsuitMat', alt: 'Wetsuit Mat' },
    { type: 'image', name: 'cricket', alt: 'Cricket Toy' },
  ];
  const preloadVideoNames = React.useMemo(() => {
    const names = [];
    for (const it of items) {
      if (it.type === 'video') names.push(it.name);
      if (names.length >= 3) break;
    }
    return new Set(names);
  }, [items]);
  React.useEffect(() => {
    const container = document.getElementById('archive-gallery');
    if (!container) return;
    const vids = Array.from(container.querySelectorAll('video.gallery-item'));

    const ensureAttrs = (v) => {
      try {
        v.muted = true;
        v.setAttribute('muted', '');
        v.playsInline = true;
        v.setAttribute('playsinline', '');
      } catch (_) {}
    };

    const onIntersect = (entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        ensureAttrs(v);
        if (entry.isIntersecting) {
          try { const p = v.play(); if (p && typeof p.then === 'function') p.catch(() => {}); } catch (_) {}
        } else {
          try { v.pause(); } catch (_) {}
        }
      });
    };

    const io = new IntersectionObserver(onIntersect, { root: null, rootMargin: '300px 0px', threshold: 0.01 });
    vids.forEach((v) => io.observe(v));
    // Immediately attempt to play any already in view
    const playIfInView = () => {
      vids.forEach((v) => {
        const r = v.getBoundingClientRect();
        const inView = r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
        if (inView) {
          try { const p = v.play(); if (p && typeof p.then === 'function') p.catch(() => {}); } catch (_) {}
        }
      });
    };
    try { playIfInView(); } catch(_) {}
    return () => { try { io.disconnect(); } catch (_) {} };
  }, []);

  // Preload top posters eagerly via <link rel="preload"> for faster first paint
  React.useEffect(() => {
    const container = document.getElementById('archive-gallery');
    if (!container) return;
    const posters = Array.from(container.querySelectorAll('video.gallery-item'))
      .map((v) => v.getAttribute('poster'))
      .filter(Boolean);
    const head = document.head || document.getElementsByTagName('head')[0];
    const links = [];
    posters.slice(0, 6).forEach((url) => {
      try {
        const l = document.createElement('link');
        l.rel = 'preload';
        l.as = 'image';
        l.href = url;
        try { l.fetchPriority = 'high'; } catch(_) {}
        head.appendChild(l);
        links.push(l);
      } catch (_) {}
    });
    return () => {
      links.forEach((l) => { try { head.removeChild(l); } catch(_) {} });
    };
  }, []);
  return (
    <ProjectDetail title={meta.title} date={meta.date} role={meta.role} url={meta.url}>
      <div
        id="archive-gallery"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(var(--archive-cols, 2), 1fr)',
          gap: '8px'
        }}
      >
        {items.map((item) => (
          <div key={item.name} style={{ display: 'flex', flexDirection: 'column' }}>
            {item.type === 'video' ? (
              <Figure caption={item.alt}>
                <div className="video-mask">
                  <video
                    src={toCdn(`/images/projects/archive/${item.name}.mp4`)}
                    poster={toCdn(`/images/projects/archive/${item.name}.webp`)}
                    alt={item.alt}
                    className="gallery-item"
                    preload={preloadVideoNames.has(item.name) ? 'auto' : 'metadata'}
                    autoPlay
                    muted
                    playsInline
                    loop
                    style={{ aspectRatio: '8 / 5', objectFit: 'cover' }}
                  />
                </div>
              </Figure>
            ) : (
              <Figure caption={item.alt}>
                <ProgressiveImg
                  src={toCdn(`/images/projects/archive/${item.name}.webp`)}
                  alt={item.alt}
                  className="gallery-item"
                  style={{ aspectRatio: '8 / 5', objectFit: 'cover' }}
                />
              </Figure>
            )}
          </div>
        ))}
      </div>
    </ProjectDetail>
  );
}
